import { KeyboardEvent, useRef } from "react";
import { HiCheck } from "react-icons/hi";
import type { DirecionadorTransversal } from "utils/direcionadores";

interface DirecionadorCardsProps {
  direcionadores: DirecionadorTransversal[];
  carregando: boolean;
  falhou: boolean;
  onTentarNovamente: () => void;
  value: string;
  onChange: (id: string) => void;
  error?: string;
}

/** Direcionadores como radio group acessível (setas mudam a seleção): um embaixo do outro, com check no escolhido. */
export function DirecionadorCards({
  direcionadores,
  carregando,
  falhou,
  onTentarNovamente,
  value,
  onChange,
  error,
}: DirecionadorCardsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const indiceSelecionado = direcionadores.findIndex((d) => d._id === value);

  const moverSelecao = (e: KeyboardEvent, indice: number) => {
    const passo = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!passo) return;
    e.preventDefault();
    const total = direcionadores.length;
    const proximo = (indice + passo + total) % total;
    onChange(direcionadores[proximo]._id);
    refs.current[proximo]?.focus();
  };

  if (carregando) {
    return (
      <p className="text-gray-bb-2" role="status">
        Carregando direcionadores...
      </p>
    );
  }

  if (falhou || direcionadores.length === 0) {
    return (
      <p className="text-sm text-red-600" role="alert">
        Não foi possível carregar os direcionadores.{" "}
        <button type="button" onClick={onTentarNovamente} className="underline font-semibold">
          Tentar novamente
        </button>
      </p>
    );
  }

  return (
    <div>
      <div
        role="radiogroup"
        aria-label="Direcionador transversal"
        aria-describedby={error ? "direcionador-erro" : undefined}
        className="flex flex-col gap-3"
      >
        {direcionadores.map((direcionador, indice) => {
          const selecionado = direcionador._id === value;
          const focavel = selecionado || (indiceSelecionado < 0 && indice === 0);
          return (
            <button
              key={direcionador._id}
              ref={(el) => {
                refs.current[indice] = el;
              }}
              type="button"
              role="radio"
              aria-checked={selecionado}
              tabIndex={focavel ? 0 : -1}
              onClick={() => onChange(direcionador._id)}
              onKeyDown={(e) => moverSelecao(e, indice)}
              className={`w-full flex items-center gap-3 text-left rounded-xl border-2 p-4 transition-all focus:outline-none focus:ring-2 focus:ring-blue-bb ${
                selecionado ? "border-blue-bb bg-blue-50 shadow-md" : "border-gray-bb-4 hover:border-blue-bb-5"
              }`}
            >
              <span
                aria-hidden="true"
                className={`flex items-center justify-center w-6 h-6 shrink-0 rounded-full border-2 ${
                  selecionado ? "border-blue-bb bg-blue-bb text-white-bb" : "border-gray-bb-4"
                }`}
              >
                {selecionado && <HiCheck />}
              </span>
              <span>
                <span className="block font-bold text-blue-bb mb-1">{direcionador.nome}</span>
                <span className="block text-sm text-gray-bb-2">{direcionador.descricao}</span>
              </span>
            </button>
          );
        })}
      </div>
      {error && (
        <p id="direcionador-erro" className="text-red-500 text-sm mt-2">
          {error}
        </p>
      )}
    </div>
  );
}
